/**
 * CookieConsent — Sprint S (Trust & Compliance)
 *
 * Slim bottom-bar cookie consent banner. Essential cookies are always
 * allowed; Getickets currently uses no analytics/tracking cookies, so the
 * choice is stored locally (localStorage) to gate any future analytics.
 * Never blocks checkout or any interaction — it's a non-modal bar.
 * Hidden on the door-scanner route (staff device UX).
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Cookie } from "lucide-react";

const STORAGE_KEY = "gt_cookie_consent"; // "all" | "essential"

export function getCookieConsent() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

const OPEN_EVENT = "gt:manage-cookies";

export function openCookieConsent() {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

/** Footer link that re-opens the consent banner so users can update their choice. */
export function ManageCookiesLink({ className = "" }) {
  return (
    <button
      type="button"
      onClick={openCookieConsent}
      className={`hover:text-[var(--gt-accent)] transition-colors text-left ${className}`}
      data-testid="footer-manage-cookies-link"
    >
      Manage cookies
    </button>
  );
}

export default function CookieConsent() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const onScanRoute = location.pathname.startsWith("/scan/");

  useEffect(() => {
    setVisible(!getCookieConsent());
  }, []);

  // "Manage cookies" footer links re-open the banner to update the choice
  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  // While the banner is shown, pad the page bottom so it never covers
  // content (e.g. Save buttons) at any viewport size.
  useEffect(() => {
    if (!visible || onScanRoute) return undefined;
    const setPad = () => {
      const el = document.querySelector('[data-testid="cookie-consent-banner"]');
      const h = (el && el.offsetHeight) || 160;
      const pad = `${h + 24}px`;
      document.body.style.paddingBottom = pad;
      // scroll-padding makes scrollIntoView land content above the banner
      document.documentElement.style.scrollPaddingBottom = pad;
    };
    const t = setTimeout(setPad, 0);
    window.addEventListener("resize", setPad);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", setPad);
      document.body.style.paddingBottom = "";
      document.documentElement.style.scrollPaddingBottom = "";
    };
  }, [visible, onScanRoute]);

  if (!visible) return null;
  if (onScanRoute) return null;

  const choose = (value) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* storage unavailable — banner will reappear next visit */
    }
    setVisible(false);
  };

  return (
    <div
      className="fixed bottom-3 left-3 right-3 sm:right-auto sm:bottom-4 sm:left-4 sm:max-w-md z-50 bg-[var(--gt-ink)] text-white border-2 border-[var(--gt-accent)] rounded-sm shadow-[6px_6px_0px_0px_rgba(0,0,0,0.35)]"
      role="region"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
    >
      <div className="px-4 py-3 flex flex-col gap-3">
        <div className="flex items-start gap-2 text-xs leading-relaxed">
          <Cookie size={16} className="mt-0.5 shrink-0 text-[var(--gt-accent)]" aria-hidden="true" />
          <span>
            We use essential cookies to run checkout and keep your session secure. No advertising or
            tracking cookies are set.{" "}
            <Link to="/privacy" className="underline underline-offset-2 hover:text-[var(--gt-accent)]" data-testid="cookie-privacy-link">
              Privacy policy
            </Link>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => choose("essential")}
            className="text-xs font-semibold px-3 py-2 border border-white/40 rounded-sm hover:border-white transition-colors"
            data-testid="cookie-essential-btn"
          >
            Essential only
          </button>
          <button
            type="button"
            onClick={() => choose("all")}
            className="text-xs font-bold uppercase tracking-widest px-4 py-2 bg-[var(--gt-accent)] rounded-sm hover:opacity-90 transition-opacity"
            data-testid="cookie-accept-btn"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
